"use client";

import { useEffect, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useUniversalAuth } from "@wtc/ui";
import { getDashboardRedirectPath } from "@/lib/logic/critical-redirects";
import { debug } from "@/lib/utils/debug";
import { ensureAppUserAfterMagicLink } from "@/lib/actions/ensure-app-user-after-magic-link";
import { LoadingSpinner } from "@/components/ui/loading";

/**
 * Inner component that uses useSearchParams(). Separated to allow Suspense
 * boundary to wrap the hook call.
 */
function LandingAuthHandlerInner() {
  const {
    isAuthenticated,
    appUser,
    isClientUser,
    isEmployee,
    isAdmin,
    isLoading,
    isInitialized,
  } = useUniversalAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleRedirect = useCallback(() => {
    if (isInitialized && !isLoading && isAuthenticated) {
      const path = getDashboardRedirectPath({
        isAuthenticated,
        isClientUser,
        isEmployee,
        isAdmin,
        user: appUser,
      });
      if (path) {
        debug.redirect("Landing page redirecting to:", path);
        router.replace(path);
      }
    }
  }, [
    isInitialized,
    isLoading,
    isAuthenticated,
    isClientUser,
    isEmployee,
    isAdmin,
    appUser,
    router,
  ]);

  useEffect(() => {
    handleRedirect();
  }, [handleRedirect]);

  useEffect(() => {
    const code = searchParams?.get("code");
    if (!code) return;

    const handleMagicLinkAuth = async () => {
      try {
        const { createClient } = await import("@/lib/supabase/client");
        const supabase = createClient();

        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          console.error("Magic link authentication error:", error);
          return;
        }

        if (data.user) {
          const result = await ensureAppUserAfterMagicLink({
            userId: data.user.id,
            email: data.user.email ?? undefined,
            emailConfirmedAt: data.user.email_confirmed_at ?? null,
            firstName: data.user.user_metadata?.first_name ?? "",
            lastName: data.user.user_metadata?.last_name ?? "",
          });
          if (result.error) {
            console.error("Error ensuring app_users record:", result.error);
          }
        }
      } catch (err) {
        console.error("Error handling magic link authentication:", err);
      }
    };

    void handleMagicLinkAuth();
  }, [searchParams]);

  // Show non-blocking overlay only when we need to (auth initializing or redirecting).
  const showOverlay = !isInitialized || isAuthenticated;
  const overlayMessage = !isInitialized
    ? "Initializing WTC Hub..."
    : "Redirecting to your dashboard...";

  return showOverlay ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-blue-50/95 to-indigo-100/95 backdrop-blur-sm"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingSpinner
        size="lg"
        message={overlayMessage}
        className="text-blue-600"
      />
    </div>
  ) : null;
}

/**
 * Client-side auth handler for the landing page.
 * Handles auth redirect and magic link authentication without blocking page render.
 * Wrapped in Suspense to allow useSearchParams() to work.
 */
export function LandingAuthHandler() {
  return (
    <Suspense fallback={null}>
      <LandingAuthHandlerInner />
    </Suspense>
  );
}
